import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { shouldRefreshToken } from '../utils/api';
import {
  registerAdminFcmForLoggedInUser,
  unregisterAdminFcmBeforeClearAuth,
  setupAdminFcmForegroundListener,
  scheduleAdminFcmKeepAlive,
  getOrCreateAdminDeviceId,
} from '../utils/adminFcm';
import {
  ensureAdminNotificationAudioUnlock,
  unlockAdminNotificationAudio,
} from '../utils/adminNotificationSound';

// Updated User type to match new API response
export type User = {
  id: string;
  name: string;
  email: string;
  role: 'MASTER_ADMIN' | 'SALES_EXECUTIVE' | 'ACCOUNTANT' | 'WRITER' | 'MARKETING' | 'SUPPORT' | 'TECH' | 'HR';
  department: string;
  phone: string;
  profile_image: string;
};

type AuthContextType = {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (
    email: string,
    password: string,
    role: User['role'],
    options?: { operatorLabel?: string }
  ) => Promise<boolean>;
  logout: () => Promise<void>;
  updateLocalUser: (partial: Partial<User>) => void;
  token: string | null;
  refreshToken: (options?: { forceLogoutOnFailure?: boolean }) => Promise<boolean>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Unlock Web Audio early so the first FCM alert after login can play a tone.
  useEffect(() => {
    return ensureAdminNotificationAudioUnlock();
  }, []);

  // Check if user was previously logged in, then refresh profile (incl. photo) from API
  useEffect(() => {
    const checkAuthStatus = async () => {
      const savedToken = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');

      if (savedToken && savedUser) {
        try {
          const parsedUser = JSON.parse(savedUser);
          setUser(parsedUser);
          setToken(savedToken);
          setIsAuthenticated(true);

          try {
            const { getMyProfile } = await import(
              '../services/MasterAdmin/EmployeeManagementService'
            );
            const res = await getMyProfile();
            const fresh = res?.user;
            if (fresh?.id) {
              const next = {
                id: String(fresh.id),
                name: String(fresh.name || parsedUser.name || ''),
                email: String(fresh.email || parsedUser.email || ''),
                role: (fresh.role || parsedUser.role) as User['role'],
                department: String(fresh.department || parsedUser.department || ''),
                phone: String(fresh.phone || parsedUser.phone || ''),
                profile_image: String(fresh.profile_image || ''),
              };
              setUser(next);
              localStorage.setItem('user', JSON.stringify(next));
            }
          } catch (profileErr) {
            console.warn('[Auth] Failed to refresh profile photo:', profileErr);
          }
        } catch (error) {
          console.error('Error parsing saved user:', error);
          localStorage.removeItem('user');
          localStorage.removeItem('token');
        }
      }
      setIsLoading(false);
    };

    void checkAuthStatus();
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !token || isLoading) return;

    registerAdminFcmForLoggedInUser().catch((err) => {
      console.warn('[Admin FCM] Registration skipped or failed:', err);
    });

    const teardownForeground = setupAdminFcmForegroundListener();
    const teardownKeepAlive = scheduleAdminFcmKeepAlive();

    // Refresh only when access token is near expiry — not every 60s (avoids race + false logouts).
    const interval = window.setInterval(() => {
      const currentToken = localStorage.getItem('token');
      if (!currentToken || !shouldRefreshToken(currentToken)) return;
      void refreshToken({ forceLogoutOnFailure: false });
    }, 5 * 60 * 1000);

    return () => {
      window.clearInterval(interval);
      teardownForeground();
      teardownKeepAlive();
    };
  }, [isAuthenticated, token, isLoading]);

  const login = async (
    email: string,
    password: string,
    role: User['role'],
    options?: { operatorLabel?: string }
  ): Promise<boolean> => {
    try {
      // Capture the login click gesture so notification audio can resume later.
      unlockAdminNotificationAudio();
      setIsLoading(true);
      const deviceId = getOrCreateAdminDeviceId();
      let deviceIdentity: Awaited<
        ReturnType<typeof import('../utils/adminDeviceIdentity').getAdminDeviceIdentity>
      > | null = null;
      try {
        const { getAdminDeviceIdentity } = await import('../utils/adminDeviceIdentity');
        deviceIdentity = await getAdminDeviceIdentity();
      } catch {
        /* best-effort */
      }
      const response = await api.post('/master-admin/login', {
        email,
        password,
        role,
        deviceId: deviceIdentity?.deviceId || deviceId,
        platform: 'web',
        operatorLabel: options?.operatorLabel?.trim() || undefined,
        deviceLabel: deviceIdentity?.deviceLabel,
        deviceModel: deviceIdentity?.deviceModel || undefined,
        deviceCode: deviceIdentity?.deviceCode,
      });
      // Response: { message, token, refreshToken, sessionId, operatorLabel, user }
      const {
        token,
        refreshToken: newRefreshToken,
        user,
        sessionId,
        operatorLabel,
      } = response.data;
      setUser(user);
      setToken(token);
      setIsAuthenticated(true);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('token', token);
      if (newRefreshToken) {
        localStorage.setItem('refreshToken', newRefreshToken);
      }
      localStorage.setItem('userId', user.id);
      if (sessionId) {
        localStorage.setItem('adminSessionId', sessionId);
      }
      if (operatorLabel) {
        localStorage.setItem('adminOperatorLabel', operatorLabel);
      } else {
        localStorage.removeItem('adminOperatorLabel');
      }
      // Refresh full profile (photo URL may be signed / updated).
      try {
        const { getMyProfile } = await import(
          '../services/MasterAdmin/EmployeeManagementService'
        );
        const profileRes = await getMyProfile();
        const fresh = profileRes?.user;
        if (fresh?.id) {
          const next = {
            id: String(fresh.id),
            name: String(fresh.name || user.name || ''),
            email: String(fresh.email || user.email || ''),
            role: (fresh.role || user.role) as User['role'],
            department: String(fresh.department || user.department || ''),
            phone: String(fresh.phone || user.phone || ''),
            profile_image: String(fresh.profile_image || user.profile_image || ''),
          };
          setUser(next);
          localStorage.setItem('user', JSON.stringify(next));
        }
      } catch (profileErr) {
        console.warn('[Auth] Post-login profile refresh failed:', profileErr);
      }
      // Store user id and token in api if needed (for global access)
      // (If you want to export these from api.ts, you can do so, but axios interceptor already uses localStorage)
      sessionStorage.setItem('isLoggedIn', 'true');
      // Try once immediately on login click (better chance for browser permission flow).
      try {
        await registerAdminFcmForLoggedInUser();
      } catch (err) {
        console.warn('[Admin FCM] Immediate login registration failed:', err);
      }
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshToken = async (
    options: { forceLogoutOnFailure?: boolean } = {}
  ): Promise<boolean> => {
    const { forceLogoutOnFailure = false } = options;
    try {
      const storedRefreshToken = localStorage.getItem('refreshToken');
      const currentToken = localStorage.getItem('token');
      if (!storedRefreshToken && !currentToken) {
        return false;
      }

      const response = await api.post('/master-admin/auth/refresh-token', storedRefreshToken
        ? { refreshToken: storedRefreshToken }
        : { token: currentToken });

      const { token: newToken, refreshToken: newRefreshToken } = response.data;
      setToken(newToken);
      localStorage.setItem('token', newToken);
      if (newRefreshToken) {
        localStorage.setItem('refreshToken', newRefreshToken);
      }
      return true;
    } catch (error: any) {
      const status = error?.response?.status;
      const isAuthFailure = status === 401 || status === 403;
      console.error('Token refresh failed:', error);

      // Only logout when refresh token is actually invalid — not on transient network errors.
      if (forceLogoutOnFailure || isAuthFailure) {
        await logout();
      }
      return false;
    }
  };

  const updateLocalUser = (partial: Partial<User>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...partial };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  };

  const logout = async (): Promise<void> => {
    console.log('[Auth] Logout started');
    const sessionId = localStorage.getItem('adminSessionId');
    const deviceId = localStorage.getItem('vedika_admin_fcm_device_id');
    const token = localStorage.getItem('token');
    try {
      if (token) {
        await api.post(
          '/master-admin/logout',
          {
            sessionId: sessionId || undefined,
            deviceId: deviceId || undefined,
            reason: 'user_logout',
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
    } catch (err) {
      console.warn('[Auth] Logout session close failed', err);
    }
    await unregisterAdminFcmBeforeClearAuth();
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('adminSessionId');
    localStorage.removeItem('adminOperatorLabel');
    sessionStorage.removeItem('isLoggedIn');
    console.log('[Auth] Logout completed');
  };

  // Listen for storage events (for multi-tab support)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'token' && !event.newValue) {
        // User logged out in another tab
        setUser(null);
        setToken(null);
        setIsAuthenticated(false);
      } else if (event.key === 'user' && event.newValue) {
        // User logged in in another tab
        try {
          const parsedUser = JSON.parse(event.newValue);
          setUser(parsedUser);
          setIsAuthenticated(true);
          
          // Also check if token exists
          const currentToken = localStorage.getItem('token');
          if (currentToken) {
            setToken(currentToken);
          }
        } catch (error) {
          console.error('Error parsing user from storage event:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, isAuthenticated, isLoading, login, logout, refreshToken, updateLocalUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default useAuth; 